var _dec, _dec2, _class, _TopNavBarItem, _IconArrowOpenUpSolid, _IconArrowOpenDownSol;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Component } from 'react';
import { callRenderProp, matchComponentTypes, omitProps, passthroughProps, safeCloneElement, withDeterministicId } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { warn, error } from '@instructure/console';
import { withStyle, InstUISettingsProvider } from '@instructure/emotion';
import { IconArrowOpenDownSolid, IconArrowOpenUpSolid } from '@instructure/ui-icons';
import { Avatar } from '@instructure/ui-avatar';
import { BaseButton } from '@instructure/ui-buttons';
import { Tooltip } from '@instructure/ui-tooltip';
import { Popover } from '@instructure/ui-popover';
import { Drilldown } from '@instructure/ui-drilldown';
import { ScreenReaderContent } from '@instructure/ui-a11y-content';
import { TopNavBarContext } from "../TopNavBarContext.js";
import generateStyle from "./styles.js";
import generateComponentTheme from "./theme.js";
import { allowedProps } from "./props.js";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const navbarStackingDefault = 10000;
const submenuStacking = navbarStackingDefault + 1;
const tooltipStacking = navbarStackingDefault + 2;

/**
---
parent: TopNavBar
id: TopNavBar.Item
---
@module TopNavBarItem
**/
let TopNavBarItem = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_TopNavBarItem = class TopNavBarItem extends Component {
  constructor(props) {
    super(props);
    this.ref = null;
    this._itemRef = null;
    this._tooltipContentId = void 0;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.handleItemRef = el => {
      const itemRef = this.props.itemRef;
      this._itemRef = el;
      if (typeof itemRef === 'function') {
        itemRef(el);
      }
    };
    this.onFocus = () => {
      this.setState({
        isFocused: true
      });
    };
    this.onBlur = () => {
      this.setState({
        isFocused: false
      });
    };
    this.handleKeyDown = e => {
      if (e.key === 'ArrowDown') {
        if (this.shouldRenderSubmenu && !this.state.isSubmenuOpen || this.shouldRenderPopover && !this.state.isPopoverOpen) {
          var _this$_itemRef;
          e.preventDefault();
          (_this$_itemRef = this._itemRef) === null || _this$_itemRef === void 0 ? void 0 : _this$_itemRef.click();
        }
      }
      if (e.key === 'Tab') {
        if (this.state.isPopoverOpen && !this.isCustomPopoverControlled) {
          this.setState({
            isPopoverOpen: false
          });
        }
      }
    };
    this.handleMenuToggle = (event, args) => {
      this.setState({
        isSubmenuOpen: args.shown
      });
      if (typeof this.props.onSubmenuToggle === 'function') {
        this.props.onSubmenuToggle(event, args);
      }
    };
    this._tooltipContentId = props.deterministicId('TopNavBarItem-tooltip');
    this.state = {
      isSubmenuOpen: false,
      isPopoverOpen: false,
      isFocused: false
    };
  }
  componentDidMount() {
    const _this$props = this.props,
      makeStyles = _this$props.makeStyles,
      customPopoverConfig = _this$props.customPopoverConfig;
    makeStyles === null || makeStyles === void 0 ? void 0 : makeStyles(this.makeStylesVariables);
    if (customPopoverConfig && this.isCustomPopoverControlled) {
      this.setState({
        isPopoverOpen: !!customPopoverConfig.isShowingContent
      });
    }
  }
  componentDidUpdate() {
    const _this$props2 = this.props,
      makeStyles = _this$props2.makeStyles,
      customPopoverConfig = _this$props2.customPopoverConfig;
    makeStyles === null || makeStyles === void 0 ? void 0 : makeStyles(this.makeStylesVariables);
    if (customPopoverConfig && this.isCustomPopoverControlled && customPopoverConfig.isShowingContent !== this.state.isPopoverOpen) {
      this.setState({
        isPopoverOpen: !!customPopoverConfig.isShowingContent
      });
    }
  }
  get makeStylesVariables() {
    const _this$context = this.context,
      layout = _this$context.layout,
      inverseColor = _this$context.inverseColor;
    return {
      layout,
      inverseColor
    };
  }
  get shouldRenderSubmenu() {
    const renderSubmenu = this.props.renderSubmenu;
    return !!renderSubmenu && matchComponentTypes(renderSubmenu, [Drilldown]);
  }
  get shouldRenderPopover() {
    const _this$props3 = this.props,
      id = _this$props3.id,
      renderSubmenu = _this$props3.renderSubmenu,
      customPopoverConfig = _this$props3.customPopoverConfig;
    if (!customPopoverConfig) {
      return false;
    }
    if (!customPopoverConfig.children) {
      warn(false, `Pass the content of the custom Popover as "customPopoverConfig.children" for the item with id: "${id}".`);
      return false;
    }
    if (customPopoverConfig && renderSubmenu) {
      warn(false, `TopNavBar.Items are not allowed to have both the "renderSubmenu" and "customPopoverConfig" props. For submenus, pass a Drilldown component via the "renderSubmenu" prop, and only use "customPopoverConfig" for custom features. Item with id: "${id}" will ignore the "customPopoverConfig" prop.`);
      return false;
    }
    return true;
  }
  get isCustomPopoverControlled() {
    const customPopoverConfig = this.props.customPopoverConfig;
    return customPopoverConfig && typeof customPopoverConfig.isShowingContent !== 'undefined';
  }
  get hasOpenPopover() {
    return this.state.isSubmenuOpen || this.state.isPopoverOpen;
  }
  get isDefaultVariant() {
    return this.props.variant === 'default' || this.props.variant === 'forceIconWithLabel';
  }
  get isButtonVariant() {
    return this.props.variant === 'button';
  }
  get isIconVariant() {
    return this.props.variant === 'icon';
  }
  get isAvatarOnlyVariant() {
    return this.props.variant === 'avatar';
  }
  get hasAvatar() {
    const _this$props4 = this.props,
      id = _this$props4.id,
      renderIcon = _this$props4.renderIcon,
      renderAvatar = _this$props4.renderAvatar,
      variant = _this$props4.variant;
    const hasAvatar = !!renderAvatar;
    if (this.isAvatarOnlyVariant && !hasAvatar) {
      warn(false, `The "renderAvatar" config is required for the 'variant="avatar"' type <TopNavBar.Item> components, but received none for the item with id "${id}".`);
      return false;
    }
    if (hasAvatar && (renderIcon || variant === 'icon')) {
      warn(false, `<TopNavBar.Item> components with icons cannot display avatars, so the "renderAvatar" config prop will be ignored for item with id "${id}".`);
      return false;
    }
    return hasAvatar;
  }
  get submenuIcon() {
    var _this$props$styles;
    if (!(this.shouldRenderSubmenu || this.shouldRenderPopover) || !this.props.showSubmenuChevron) {
      return null;
    }
    return _jsx("span", {
      css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.submenuIcon,
      children: this.shouldRenderSubmenu && this.state.isSubmenuOpen || this.shouldRenderPopover && this.state.isPopoverOpen ? _IconArrowOpenUpSolid || (_IconArrowOpenUpSolid = _jsx(IconArrowOpenUpSolid, {})) : _IconArrowOpenDownSol || (_IconArrowOpenDownSol = _jsx(IconArrowOpenDownSolid, {}))
    });
  }
  get buttonThemeOverride() {
    const styles = this.props.styles;
    const themeOverride = {};
    if (this.isDefaultVariant || this.hasAvatar) {
      themeOverride.mediumPaddingHorizontal = styles === null || styles === void 0 ? void 0 : styles.itemInlinePadding;
    }
    if (this.hasAvatar) {
      themeOverride.mediumPaddingTop = '0.125rem';
      themeOverride.mediumPaddingBottom = '0.125rem';
    }
    if (this.isAvatarOnlyVariant && !this.submenuIcon) {
      themeOverride.mediumPaddingHorizontal = '0';
      themeOverride.mediumPaddingTop = '0';
      themeOverride.mediumPaddingBottom = '0';
    }
    return Object.keys(themeOverride).length > 0 ? themeOverride : void 0;
  }
  get colorProps() {
    let color = 'primary-inverse';
    let focusColor = 'inverse';
    if (this.context.inverseColor) {
      color = 'secondary';
      focusColor = 'info';
      if (this.isButtonVariant) {
        color = 'primary';
      }
    }
    return {
      color,
      focusColor
    };
  }
  get ariaProps() {
    let hasPopup;
    let expanded;
    if (this.shouldRenderSubmenu) {
      hasPopup = 'menu';
      expanded = this.state.isSubmenuOpen;
    }
    if (this.shouldRenderPopover) {
      hasPopup = 'true';
      expanded = this.state.isPopoverOpen;
    }
    return {
      'aria-haspopup': hasPopup,
      'aria-expanded': expanded
    };
  }
  get itemProps() {
    const _this$props5 = this.props,
      id = _this$props5.id,
      as = _this$props5.as,
      variant = _this$props5.variant,
      hrefOriginal = _this$props5.href,
      onClickOriginal = _this$props5.onClick,
      onMouseOver = _this$props5.onMouseOver,
      onMouseOut = _this$props5.onMouseOut,
      onFocus = _this$props5.onFocus,
      onBlur = _this$props5.onBlur,
      onKeyDown = _this$props5.onKeyDown,
      onKeyUp = _this$props5.onKeyUp,
      renderSubmenu = _this$props5.renderSubmenu,
      statusOriginal = _this$props5.status,
      renderAvatar = _this$props5.renderAvatar,
      renderIcon = _this$props5.renderIcon,
      withFocusOutline = _this$props5.withFocusOutline;
    let href = hrefOriginal;
    let onClick = onClickOriginal;
    let status = statusOriginal;
    if (renderSubmenu) {
      if (href) {
        warn(false, `TopNavBar.Items with submenus are not allowed to have 'href' property, but received href "${href}" for item with the id: "${id}".`);
        href = void 0;
      }
      if (onClick) {
        warn(false, `TopNavBar.Items with submenus are not allowed to have 'onClick' property, but received onClick for item with the id: "${id}".Use the \`onSubmenuToggle\` prop instead. OnClick: ${onClick}`);
        onClick = void 0;
      }
    }
    if (status === 'active' && !this.isDefaultVariant) {
      warn(false, `Only \`variant="default"\` <TopNavBar.Item> components can be set to active, but item with id "${id}" has variant: "${variant}".`);
      status = 'default';
    }
    if (this.hasAvatar) {
      if (!(renderAvatar !== null && renderAvatar !== void 0 && renderAvatar.avatarName)) {
        error(false, `The "avatarName" prop is required for for <TopNavBar.Item> components with avatar, but the item with id "${id}" is missing it.`);
        return null;
      }
      if (status === 'active') {
        warn(false, `<TopNavBar.Item> components with avatar cannot have "active" status, so the "active" status on the item with id "${id}" will be ignored.`);
        status = 'default';
      }
    }
    if (this.isIconVariant) {
      if (!renderIcon) {
        error(false, `The "renderIcon" prop is required for the \`variant="icon"\` type <TopNavBar.Item> components, but the item with id "${id}" is missing it.`);
        return null;
      }
    }
    return {
      id,
      as,
      ...this.colorProps,
      ...this.ariaProps,
      size: 'medium',
      withBorder: this.isButtonVariant,
      withBackground: this.isButtonVariant,
      interaction: status === 'disabled' ? 'disabled' : void 0,
      'aria-disabled': status === 'disabled' ? 'true' : void 0,
      shape: this.isAvatarOnlyVariant && !this.submenuIcon ? 'circle' : 'rectangle',
      href,
      onClick,
      onMouseOver,
      onMouseOut,
      onFocus: createChainedFunction(onFocus, this.onFocus),
      onBlur: createChainedFunction(onBlur, this.onBlur),
      onKeyDown: createChainedFunction(onKeyDown, this.handleKeyDown),
      onKeyUp,
      renderIcon,
      themeOverride: this.buttonThemeOverride,
      elementRef: e => {
        this.handleItemRef(e);
      },
      withFocusOutline: withFocusOutline || this.hasOpenPopover ? true : void 0
    };
  }
  renderContent() {
    var _this$props$styles2;
    const _this$props6 = this.props,
      children = _this$props6.children,
      styles = _this$props6.styles;
    const itemProps = this.itemProps;
    if (!itemProps) {
      return null;
    }
    let content = children;
    if (this.isAvatarOnlyVariant) {
      content = this.renderAvatar();
    } else if (this.isIconVariant) {
      content = _jsx(ScreenReaderContent, {
        children: content
      });
    }
    const button = _jsx(BaseButton, {
      ...itemProps,
      ...passthroughProps(omitProps(this.props, allowedProps)),
      children: this.isIconVariant && !this.submenuIcon ? content : _jsxs("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.content,
        children: [this.hasAvatar && !this.isAvatarOnlyVariant && _jsx("span", {
          css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.avatarContainer,
          children: this.renderAvatar()
        }), _jsx("span", {
          css: styles === null || styles === void 0 ? void 0 : styles.contentText,
          children: content
        }), this.submenuIcon]
      })
    });
    return _jsx("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.container,
      children: this.props.tooltip ? this.wrapButtonInTooltip(button, children) : button
    });
  }
  wrapButtonInTooltip(button, content) {
    const _this$props7 = this.props,
      tooltip = _this$props7.tooltip,
      styles = _this$props7.styles;
    if (!tooltip) {
      return button;
    }
    let tooltipProps = {
      placement: 'bottom',
      color: this.context.inverseColor ? 'primary' : 'primary-inverse'
    };
    if (typeof tooltip === 'string') {
      tooltipProps.renderTip = tooltip;
    } else {
      tooltipProps = {
        ...tooltipProps,
        ...tooltip
      };
    }
    const tipContent = callRenderProp(tooltipProps.renderTip);
    if (!tipContent) {
      return button;
    }
    const contentAndTooltipIdentical = tipContent === content;
    return _jsx(InstUISettingsProvider, {
      theme: {
        componentOverrides: {
          View: {
            // moves tooltips above submenus/popovers
            stackingTopmost: tooltipStacking
          }
        }
      },
      children: _jsx(Tooltip, {
        ...tooltipProps,
        renderTip: contentAndTooltipIdentical ? tipContent : _jsx("div", {
          id: this._tooltipContentId,
          children: tipContent
        }),
        positionContainerDisplay: "block",
        children: _jsx("div", {
          css: styles === null || styles === void 0 ? void 0 : styles.submenuTriggerContainer,
          children: contentAndTooltipIdentical ? button : safeCloneElement(button, {
            'aria-describedby': this._tooltipContentId
          })
        })
      })
    });
  }
  renderAvatar() {
    if (!this.hasAvatar) {
      return null;
    }
    const _this$props8 = this.props,
      id = _this$props8.id,
      children = _this$props8.children,
      renderAvatar = _this$props8.renderAvatar;
    const _ref = renderAvatar,
      avatarName = _ref.avatarName,
      avatarSrc = _ref.avatarSrc,
      avatarAlt = _ref.avatarAlt;
    const label = avatarAlt || (typeof children === 'string' ? children : void 0);
    if (!label) {
      warn(false, `Please supply a label for the avatar with either the "renderAvatar.avatarAlt" or the "children" (as string) prop. It is needed for screen reader support, but missing on the item with the id: "${id}".`);
    }
    return avatarName ? _jsx(Avatar, {
      name: avatarName,
      src: avatarSrc,
      alt: label,
      size: "small"
    }) : null;
  }
  renderDropdownMenu() {
    var _renderSubmenu$props, _renderSubmenu$props2, _renderSubmenu$props3;
    const _this$props9 = this.props,
      id = _this$props9.id,
      renderSubmenu = _this$props9.renderSubmenu,
      status = _this$props9.status,
      styles = _this$props9.styles;
    if (!renderSubmenu || !this.shouldRenderSubmenu) {
      return null;
    }
    if (typeof renderSubmenu.props.show !== 'undefined') {
      warn(false, `TopNavBar.Item Drilldown submenus are controlled by the component. The "show" prop will be ignored on the submenu of the item with id: "${id}".`);
    }
    if (renderSubmenu.props.trigger) {
      warn(false, `TopNavBar.Item submenus have the item itself as their trigger. The "trigger" prop will be ignored on the Drilldown submenu of item with id: "${id}".`);
    }
    return safeCloneElement(renderSubmenu, {
      trigger: _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.submenuTriggerContainer,
        children: this.renderContent()
      }),
      show: this.state.isSubmenuOpen,
      onToggle: createChainedFunction((_renderSubmenu$props = renderSubmenu.props) === null || _renderSubmenu$props === void 0 ? void 0 : _renderSubmenu$props.onToggle, this.handleMenuToggle),
      positionContainerDisplay: 'block',
      placement: 'bottom start',
      withArrow: false,
      minWidth: ((_renderSubmenu$props2 = renderSubmenu.props) === null || _renderSubmenu$props2 === void 0 ? void 0 : _renderSubmenu$props2.minWidth) || '18.5rem',
      maxHeight: ((_renderSubmenu$props3 = renderSubmenu.props) === null || _renderSubmenu$props3 === void 0 ? void 0 : _renderSubmenu$props3.maxHeight) || `calc(100vh - 10rem)`,
      // this is needed because the trigger is not a button. `aria-expanded`
      // is set on `get itemProps()`
      shouldSetAriaExpanded: false,
      ...(status === 'disabled' && {
        disabled: true,
        show: false,
        onToggle: void 0
      })
    });
  }
  renderPopover() {
    const _this$props0 = this.props,
      id = _this$props0.id,
      customPopoverConfig = _this$props0.customPopoverConfig,
      styles = _this$props0.styles;
    if (!customPopoverConfig || !this.shouldRenderPopover) {
      return null;
    }

    // @ts-expect-error throw a warning just in case they pass trigger
    if (customPopoverConfig.renderTrigger) {
      warn(false, `TopNavBar.Item popovers have the item itself as their trigger. The "renderTrigger" prop will be ignored on the popover of item with id: "${id}".`);
    }
    const popoverProps = {
      // defaults:
      placement: 'bottom start',
      withArrow: true,
      shouldReturnFocus: true,
      shouldContainFocus: false,
      // user input:
      ...customPopoverConfig,
      // fix overrides:
      positionContainerDisplay: 'block',
      renderTrigger: _jsx("div", {
        css: styles === null || styles === void 0 ? void 0 : styles.submenuTriggerContainer,
        children: this.renderContent()
      }),
      onShowContent: createChainedFunction(customPopoverConfig.onShowContent, () => {
        if (!this.isCustomPopoverControlled) {
          this.setState({
            isPopoverOpen: true
          });
        }
      }),
      onHideContent: createChainedFunction(customPopoverConfig.onHideContent, () => {
        if (!this.isCustomPopoverControlled) {
          this.setState({
            isPopoverOpen: false
          });
        }
      }),
      isShowingContent: this.state.isPopoverOpen,
      // this is needed because the trigger is not a button. `aria-expanded`
      // is set on `get itemProps()`
      shouldSetAriaExpanded: false
    };
    return _jsx(Popover, {
      ...popoverProps,
      children: customPopoverConfig.children
    });
  }
  render() {
    const styles = this.props.styles;
    let content;
    if (this.shouldRenderPopover) {
      content = _jsx(InstUISettingsProvider, {
        theme: {
          componentOverrides: {
            View: {
              // moves popovers above navbar, below tooltips
              stackingTopmost: submenuStacking
            }
          }
        },
        children: this.renderPopover()
      });
    } else if (this.shouldRenderSubmenu) {
      content = _jsx(InstUISettingsProvider, {
        theme: {
          componentOverrides: {
            View: {
              // moves submenus above navbar, below tooltips
              stackingTopmost: submenuStacking
            }
          }
        },
        children: this.renderDropdownMenu()
      });
    } else {
      content = this.renderContent();
    }
    return _jsx("div", {
      ref: this.handleRef,
      css: styles === null || styles === void 0 ? void 0 : styles.topNavBarItem,
      "data-cid": "TopNavBarItem",
      children: content
    });
  }
}, _TopNavBarItem.displayName = "TopNavBarItem", _TopNavBarItem.componentId = 'TopNavBar.Item', _TopNavBarItem.allowedProps = allowedProps, _TopNavBarItem.defaultProps = {
  status: 'default',
  variant: 'default',
  showSubmenuChevron: true,
  shouldCloseOnClick: 'auto'
}, _TopNavBarItem.contextType = TopNavBarContext, _TopNavBarItem)) || _class) || _class);
export { TopNavBarItem };
export default TopNavBarItem;